import { useEffect, useRef, useState } from 'react';

import { getSiteCmsCompetitionCardsApi, listSiteCmsDocumentsApi, listSiteCmsImagesApi } from '@/lib/api/siteCmsApi';
import { cmsCompetitionReferences, newCmsSection } from '@shared/cms/content';
import { cmsPageReferences } from '@shared/cms/links';
import type { SiteCmsBlock, SiteCmsDraftResponse, SiteCmsPageDraft } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ChevronDown,
  Copy,
  Eye,
  FilePlus,
  Home,
  ListPlus,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  Type,
} from 'lucide-react';

import { SiteCmsDialog } from './SiteCmsDialog';
import SiteCmsSectionEditor from './SiteCmsSectionEditor';
import { nextCmsPageSlug, renameCmsPage } from './site-cms-editor-utils';
import { type CmsPageFilter, type CmsPageSort, cmsPageTableRows } from './site-cms-page-table-model';
import { cmsAddPageToMenu, cmsDuplicatePage, cmsMovePage, cmsPageCounts, cmsPageInMenu } from './site-cms-pages-model';
import {
  type CmsStructureUndo,
  cmsIdentity,
  cmsKeepIdentity,
  cmsSectionSummary,
  cmsStructureCheckpoint,
  undoCmsStructure,
} from './site-cms-workspace-model';
import { useCmsInteractions, useCmsPendingForm } from './use-cms-interactions';

const sectionTypes = [
  ['cover', 'Couverture', 'Une introduction avec image et action'],
  ['text', 'Texte enrichi', 'Paragraphes, listes et liens'],
  ['imageText', 'Image & texte', 'Présenter votre association'],
  ['info', 'Informations pratiques', 'Horaires, lieu et accès'],
  ['documents', 'Documents', 'Choisir les PDF à partager'],
  ['competitions', 'Concours', 'De vraies fiches BoulesConnect'],
  ['partners', 'Partenaires', 'Vos soutiens et leurs logos'],
  ['cta', 'Appel à l’action', 'Inviter le visiteur à poursuivre'],
] as const;
const labels: Record<SiteCmsBlock['type'], string> = {
  heading: 'Titre',
  paragraph: 'Paragraphe',
  button: 'Bouton',
  cover: 'Couverture',
  text: 'Texte enrichi',
  imageText: 'Image & texte',
  info: 'Informations pratiques',
  documents: 'Documents',
  competitions: 'Concours BoulesConnect',
  partners: 'Partenaires',
  cta: 'Appel à l’action',
};

type Props = {
  draft: SiteCmsDraftResponse;
  onChange: (draft: SiteCmsDraftResponse) => void;
  selected: string;
  onSelect: (slug: string) => void;
  onPreview?: (slug: string) => void;
  focusRequest?: { pageSlug?: string; blockIndex?: number; serial: number };
  openRequest?: number;
};
export default function SiteCmsPageManager({
  draft,
  onChange,
  selected,
  onSelect,
  onPreview,
  focusRequest,
  openRequest = 0,
}: Props) {
  const { ask } = useCmsInteractions();
  const owner = [draft.ownerType, draft.ownerId] as const;
  const images = useQuery({
    queryKey: ['site-cms', 'images', ...owner],
    queryFn: () => listSiteCmsImagesApi(...owner),
    retry: false,
  });
  const docs = useQuery({
    queryKey: ['site-cms', 'documents', ...owner],
    queryFn: () => listSiteCmsDocumentsApi(...owner),
    retry: false,
  });
  const ids = cmsCompetitionReferences(draft.pages);
  const competitions = useQuery({
    queryKey: ['site-cms', 'competition-cards', ...owner, ids.join(',')],
    queryFn: () => getSiteCmsCompetitionCardsApi(...owner, ids),
    retry: false,
  });
  const [template, setTemplate] = useState('presentation');
  const [newTitle, setNewTitle] = useState('');
  const [addresses, setAddresses] = useState<Record<string, string>>({});
  useCmsPendingForm(
    'page-address',
    draft.pages.some(page => addresses[page.slug] !== undefined && addresses[page.slug].trim() !== page.slug),
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CmsPageFilter>('all');
  const [sort, setSort] = useState<CmsPageSort>('position');
  const [view, setView] = useState<'index' | 'editor'>('index');
  const [newPageOpen, setNewPageOpen] = useState(false);
  const [insertion, setInsertion] = useState<number | null>(null);
  const [expandedState, setExpandedState] = useState<{ id: string | null; request: number }>({ id: null, request: 0 });
  const requestSerial = focusRequest?.serial ?? 0;
  const expanded = expandedState.request === requestSerial ? expandedState.id : null;
  const focusBlock =
    !focusRequest?.pageSlug || focusRequest.pageSlug === selected ? focusRequest?.blockIndex : undefined;
  const setExpanded = (id: string | null) => setExpandedState({ id, request: requestSerial });
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (requestSerial) setView('editor');
  }, [requestSerial]);
  useEffect(() => {
    if (openRequest) setView('editor');
  }, [openRequest]);
  useEffect(() => {
    if (view !== 'editor' || !requestSerial || (focusRequest?.pageSlug && focusRequest.pageSlug !== selected)) return;
    const frame = requestAnimationFrame(() => {
      const target =
        focusBlock === undefined
          ? root.current?.querySelector<HTMLDetailsElement>('.cms-page-settings')
          : root.current?.querySelector<HTMLElement>(`[data-block-index="${focusBlock}"]`);
      if (target instanceof HTMLDetailsElement) target.open = true;
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.querySelector<HTMLElement>('input,textarea,select,button')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [requestSerial, focusBlock, selected, focusRequest?.pageSlug, view]);
  const [undo, setUndo] = useState<{ slug: string; revision: number; value: CmsStructureUndo } | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  useCmsPendingForm('new-page', newPageOpen && Boolean(newTitle.trim()));
  const page = draft.pages.find(p => p.slug === selected) ?? draft.pages[0];
  if (!page) return <p>Aucune page disponible.</p>;
  const coverIndex = page.slug === 'home' ? page.blocks.findIndex(b => b.type === 'cover') : -1;
  const primaryCover = coverIndex >= 0 ? page.blocks[coverIndex] : undefined;
  const counts = cmsPageCounts(draft);
  const visible = cmsPageTableRows(draft, search, filter, sort);
  const ordered = draft.pages.filter(p => p.slug !== 'home');
  const references = cmsPageReferences(draft.pages, draft.headerMenu, page.slug);
  const inMenu = cmsPageInMenu(draft.headerMenu, page.slug);
  function openPage(slug: string) {
    onSelect(slug);
    setView('editor');
    setExpanded(null);
    setError('');
    setMessage('');
  }
  function duplicatePage(slug = page.slug) {
    try {
      const copy = cmsDuplicatePage(draft, slug);
      onChange(copy.draft);
      openPage(copy.page.slug);
      setFilter('all');
      setSearch('');
      setExpanded(null);
      setError('');
      setMessage(
        'Copie créée en privé, sans ajout automatique au menu. Les fichiers restent partagés dans la bibliothèque.',
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Copie impossible');
    }
  }
  function addToMenu(slug = page.slug) {
    try {
      onChange(cmsAddPageToMenu(draft, slug));
      setError('');
      setMessage('Lien ajouté à la fin du menu principal. Vous pouvez l’organiser dans Navigation.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ajout au menu impossible');
    }
  }
  function applyAddress() {
    const next = (addresses[page.slug] ?? page.slug).trim();
    if (next === page.slug) return;
    try {
      onChange(renameCmsPage(draft, page.slug, next));
      onSelect(next);
      setAddresses(previous => {
        const copy = { ...previous };
        delete copy[page.slug];
        return copy;
      });
      setError('');
      setMessage(
        'Adresse appliquée au brouillon. Les liens internes suivent le changement ; les anciens liens externes ne sont pas redirigés.',
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Adresse invalide');
    }
  }
  function updatePage(next: SiteCmsPageDraft) {
    onChange({ ...draft, pages: draft.pages.map(p => (p.slug === page.slug ? next : p)) });
  }
  function structure(blocks: SiteCmsBlock[], announcement: string) {
    setUndo({ slug: page.slug, revision: draft.revision, value: cmsStructureCheckpoint(page.blocks, blocks) });
    updatePage({ ...page, blocks });
    setMessage(announcement);
  }
  function change(index: number, block: SiteCmsBlock) {
    updatePage({ ...page, blocks: page.blocks.map((old, at) => (at === index ? cmsKeepIdentity(old, block) : old)) });
  }
  function move(index: number, step: number) {
    const positions = page.blocks.map((_, i) => i).filter(i => i !== coverIndex);
    const target = positions[positions.indexOf(index) + step];
    if (target === undefined) return;
    const blocks = [...page.blocks];
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    structure(blocks, 'Ordre des sections modifié. Le contenu est conservé.');
  }
  function addPage() {
    if (!newTitle.trim() || draft.pages.length >= 30) return;
    const title = newTitle.trim(),
      slug = nextCmsPageSlug(title, draft.pages);
    const blocks =
      template === 'practical'
        ? [newCmsSection('info'), newCmsSection('documents')]
        : template === 'competitions'
          ? [newCmsSection('competitions'), newCmsSection('cta')]
          : template === 'blank'
            ? []
            : [newCmsSection('imageText'), newCmsSection('text')];
    onChange({ ...draft, pages: [...draft.pages, { slug, title, description: '', visibility: 'DRAFT', blocks }] });
    setNewTitle('');
    setNewPageOpen(false);
    setSearch('');
    setFilter('all');
    openPage(slug);
    setError('');
    setMessage('Page créée en privé. Choisissez sa diffusion dans les paramètres avant de la publier.');
  }
  async function deletePage(slug = page.slug) {
    const target = draft.pages.find(p => p.slug === slug);
    if (!target || slug === 'home') return;
    const refs = cmsPageReferences(draft.pages, draft.headerMenu, slug);
    if (refs.length) {
      setError(
        `Cette page est encore utilisée : ${refs.map(r => r.label).join(' ; ')}. Corrigez ces destinations avant de la supprimer.`,
      );
      return;
    }
    if (
      (await ask({
        title: `Supprimer « ${target.title} » ?`,
        message: 'Seul le brouillon sera modifié. Une page déjà en ligne y reste jusqu’à la prochaine publication.',
        choices: [{ value: 'delete', label: 'Supprimer du brouillon', danger: true }],
      })) !== 'delete'
    )
      return;
    onChange({ ...draft, pages: draft.pages.filter(p => p.slug !== slug) });
    onSelect('home');
    setView('index');
    setError('');
    setMessage('Page retirée du brouillon. Le site public ne change pas avant publication.');
  }
  async function visibility(next: 'PUBLIC' | 'DRAFT') {
    if (next === 'DRAFT') {
      if (
        (await ask({
          title: 'Garder cette page privée ?',
          message:
            'Elle sera exclue de la prochaine publication. Ses sous-liens publics seront remontés dans le menu. La version actuellement en ligne ne change pas maintenant.',
          choices: [{ value: 'hide', label: 'Exclure à la prochaine publication' }],
        })) !== 'hide'
      )
        return;
    }
    updatePage({ ...page, visibility: next });
  }
  function blockCard(block: SiteCmsBlock, index: number, fixed = false) {
    const id = cmsIdentity(block);
    const isOpen =
      expanded === '__all__' ||
      expanded === id ||
      (expanded === null && (focusBlock === index || (focusBlock === undefined && (fixed || index === 0))));
    const positions = page.blocks.map((_, i) => i).filter(i => i !== coverIndex);
    return (
      <article
        data-block-index={index}
        className={`cms-block ${isOpen ? 'is-expanded' : ''}`}
        key={id}
        id={`cms-block-${id}`}
      >
        <div className='cms-block-header'>
          <button
            type='button'
            className='cms-section-toggle'
            aria-expanded={isOpen}
            aria-controls={`cms-fields-${id}`}
            onClick={() => setExpanded(isOpen ? '' : id)}
          >
            <span className='cms-block-number'>
              {fixed ? <Home size={17} /> : String(positions.indexOf(index) + 1).padStart(2, '0')}
            </span>
            <span>
              <strong>{fixed ? 'En-tête de l’accueil' : labels[block.type]}</strong>
              <small>{cmsSectionSummary(block)}</small>
            </span>
            <ChevronDown size={17} />
          </button>
          <div className='cms-block-actions'>
            {!fixed && (
              <>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Monter le bloc ${index + 1}`}
                  disabled={positions[0] === index}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Descendre le bloc ${index + 1}`}
                  disabled={positions.at(-1) === index}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  type='button'
                  className='cms-icon-button'
                  aria-label={`Dupliquer le bloc ${index + 1}`}
                  disabled={page.blocks.length >= 40}
                  onClick={() => {
                    const copy = structuredClone(block);
                    structure(
                      [...page.blocks.slice(0, index + 1), copy, ...page.blocks.slice(index + 1)],
                      'Section dupliquée.',
                    );
                    setExpanded(cmsIdentity(copy));
                  }}
                >
                  <Copy size={16} />
                </button>
              </>
            )}
            <button
              type='button'
              className='cms-icon-button cms-danger'
              aria-label={`Supprimer le bloc ${index + 1}`}
              onClick={() =>
                structure(
                  page.blocks.filter((_, at) => at !== index),
                  fixed
                    ? 'Couverture retirée. Le site utilisera son en-tête par défaut.'
                    : 'Section retirée. Vous pouvez annuler.',
                )
              }
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
        <div id={`cms-fields-${id}`} hidden={!isOpen} className='cms-section-fields'>
          {fixed && (
            <p className='cms-hint'>
              Cette couverture apparaît toujours en tête de l’accueil. Les sections ci-dessous sont réordonnables.
            </p>
          )}
          <SiteCmsSectionEditor
            block={block}
            onChange={next => change(index, next)}
            draft={draft}
            images={images.data ?? []}
            documents={docs.data ?? []}
            competitions={competitions.data ?? []}
          />
        </div>
      </article>
    );
  }
  return (
    <div ref={root} className='cms-page-workflow'>
      {view === 'index' ? (
        <section className='cms-card cms-pages-index' aria-label='Tableau des pages'>
          <header className='cms-pages-index-header'>
            <div>
              <span className='cms-kicker'>Gestion du contenu</span>
              <h3>Vos pages</h3>
              <p className='cms-hint'>
                Retrouvez vos pages, puis ouvrez uniquement celle que vous voulez modifier. Les modifications restent
                dans le brouillon jusqu’à la publication.
              </p>
            </div>
            <button
              type='button'
              className='cms-button cms-button-primary'
              disabled={draft.pages.length >= 30}
              onClick={() => setNewPageOpen(true)}
            >
              <FilePlus size={17} />
              Nouvelle page
            </button>
          </header>
          <div className='cms-page-toolbar'>
            <label className='cms-table-search'>
              <span>Rechercher</span>
              <span className='cms-search'>
                <Search size={16} />
                <input
                  aria-label='Rechercher une page'
                  placeholder='Titre, adresse ou description…'
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                />
              </span>
            </label>
            <label className='cms-table-filter'>
              Filtrer
              <select
                aria-label='Filtrer les pages'
                value={filter}
                onChange={event => setFilter(event.target.value as CmsPageFilter)}
              >
                <option value='all'>Toutes ({counts.all})</option>
                <option value='included'>À publier ({counts.included})</option>
                <option value='private'>Privées ({counts.private})</option>
                <option value='unlisted'>Hors menu ({counts.unlisted})</option>
              </select>
            </label>
            <label className='cms-table-sort'>
              Trier
              <select
                aria-label='Trier les pages'
                value={sort}
                onChange={event => setSort(event.target.value as CmsPageSort)}
              >
                <option value='position'>Ordre du site</option>
                <option value='name'>Nom (A–Z)</option>
                <option value='visibility'>Diffusion</option>
              </select>
            </label>
          </div>
          <div className='cms-page-table-heading'>
            <span role='status'>
              {visible.length} page{visible.length !== 1 ? 's' : ''} affichée{visible.length !== 1 ? 's' : ''} sur{' '}
              {counts.all} (maximum 30)
            </span>
            <span>« À publier » ne signifie pas encore « en ligne ».</span>
          </div>
          {error && (
            <div className='cms-alert' role='alert'>
              {error}
            </div>
          )}
          {message && (
            <p role='status' className='cms-notice'>
              {message}
            </p>
          )}
          {visible.length ? (
            <div className='cms-page-table-scroll'>
              <table className='cms-page-table'>
                <thead>
                  <tr>
                    <th scope='col'>Page</th>
                    <th scope='col'>Adresse</th>
                    <th scope='col'>Diffusion</th>
                    <th scope='col'>Menu</th>
                    <th scope='col'>Sections</th>
                    <th scope='col'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(row => (
                    <tr key={row.slug}>
                      <td data-label='Page'>
                        <button type='button' className='cms-page-table-title' onClick={() => openPage(row.slug)}>
                          {row.slug === 'home' ? <Home size={18} /> : <Type size={18} />}
                          <span>
                            <strong>{row.title || 'Sans titre'}</strong>
                            {row.slug === 'home' && <small>Page d’accueil</small>}
                            {addresses[row.slug] !== undefined && addresses[row.slug].trim() !== row.slug && (
                              <small className='cms-danger'>Adresse à valider</small>
                            )}
                          </span>
                        </button>
                      </td>
                      <td data-label='Adresse'>
                        <code>/{row.slug === 'home' ? '' : `${row.slug}/`}</code>
                      </td>
                      <td data-label='Diffusion'>
                        <span className={`cms-pill ${row.visibility === 'DRAFT' ? '' : 'cms-pill-green'}`}>
                          {row.visibility === 'DRAFT' ? 'Privée dans le brouillon' : 'Prochaine publication'}
                        </span>
                      </td>
                      <td data-label='Menu'>
                        <span className='cms-muted'>
                          {cmsPageInMenu(draft.headerMenu, row.slug) ? 'Présente' : 'Absente'}
                        </span>
                      </td>
                      <td data-label='Sections'>
                        <span className='cms-counter'>{row.blocks.length}</span>
                      </td>
                      <td data-label='Actions'>
                        <div className='cms-page-action-group' role='group' aria-label={`Actions pour ${row.title}`}>
                          <button
                            type='button'
                            className='cms-button cms-page-edit-btn'
                            onClick={() => openPage(row.slug)}
                          >
                            <Pencil size={15} />
                            Modifier
                          </button>
                          {onPreview && (
                            <button
                              type='button'
                              className='cms-icon-button'
                              aria-label={`Prévisualiser ${row.title}`}
                              title='Aperçu'
                              onClick={() => onPreview(row.slug)}
                            >
                              <Eye size={17} />
                            </button>
                          )}
                          <button
                            type='button'
                            className='cms-icon-button'
                            aria-label={`Dupliquer ${row.title} en privé`}
                            title='Dupliquer en privé'
                            disabled={draft.pages.length >= 30}
                            onClick={() => duplicatePage(row.slug)}
                          >
                            <Copy size={17} />
                          </button>
                          <button
                            type='button'
                            className='cms-icon-button cms-danger'
                            aria-label={`Supprimer ${row.title}`}
                            title={row.slug === 'home' ? 'Impossible de supprimer la page d’accueil' : 'Supprimer'}
                            disabled={row.slug === 'home'}
                            onClick={() => void deletePage(row.slug)}
                          >
                            <Trash2 size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='cms-page-table-empty' role='status'>
              <Search size={27} />
              <strong>Aucune page ne correspond aux filtres.</strong>
              <button
                type='button'
                className='cms-button'
                onClick={() => {
                  setSearch('');
                  setFilter('all');
                }}
              >
                Afficher toutes les pages
              </button>
            </div>
          )}
          <p className='cms-hint cms-page-table-footer'>
            Les actions modifient uniquement le brouillon. Une page supprimée du brouillon peut rester visible sur le
            site jusqu’à la prochaine publication.
          </p>
          {newPageOpen && (
            <SiteCmsDialog
              title='Créer une nouvelle page'
              onClose={() => {
                setNewPageOpen(false);
                setNewTitle('');
              }}
            >
              <div className='cms-stack'>
                <label>
                  Nom de la page
                  <input
                    autoFocus
                    aria-label='Titre de la nouvelle page'
                    value={newTitle}
                    maxLength={140}
                    placeholder='Ex. Informations pratiques'
                    onChange={event => setNewTitle(event.target.value)}
                  />
                </label>
                <label>
                  Modèle de départ
                  <select value={template} onChange={event => setTemplate(event.target.value)}>
                    <option value='presentation'>Présentation</option>
                    <option value='practical'>Informations pratiques</option>
                    <option value='competitions'>Concours</option>
                    <option value='blank'>Page libre</option>
                  </select>
                </label>
                <p className='cms-hint'>
                  La page sera créée en privé, sans lien de menu. Vous pourrez la rendre publique depuis ses paramètres.
                </p>
              </div>
              <div className='cms-dialog-actions'>
                <button
                  type='button'
                  className='cms-button'
                  onClick={() => {
                    setNewPageOpen(false);
                    setNewTitle('');
                  }}
                >
                  Annuler
                </button>
                <button
                  type='button'
                  className='cms-button cms-button-primary'
                  disabled={!newTitle.trim() || draft.pages.length >= 30}
                  onClick={addPage}
                >
                  Créer et modifier
                </button>
              </div>
            </SiteCmsDialog>
          )}
        </section>
      ) : (
        <div className='cms-page-editor-screen'>
          <div className='cms-page-backbar'>
            <button
              type='button'
              className='cms-button'
              onClick={() => {
                setView('index');
                setInsertion(null);
                setExpanded(null);
              }}
            >
              <ArrowLeft size={17} />
              Retour à la liste des pages
            </button>
            <span className='cms-pill'>
              {page.visibility === 'DRAFT' ? 'Page privée' : 'Incluse à la prochaine publication'}
            </span>
          </div>
          <section className='cms-card cms-page-content' aria-label='Éditeur de page'>
            <header className='cms-card-heading'>
              <div>
                <span className='cms-kicker'>Pages / {page.slug === 'home' ? 'Accueil' : page.title}</span>
                <h3>{page.title || 'Page sans titre'}</h3>
              </div>
              <span className='cms-pill'>
                {page.visibility === 'DRAFT' ? 'Privée dans le brouillon' : 'Prochaine publication'}
              </span>
            </header>
            <div className='cms-page-actions' role='group' aria-label='Actions de la page'>
              {onPreview && (
                <button type='button' className='cms-button' onClick={() => onPreview(page.slug)}>
                  <Eye size={16} />
                  Aperçu de cette page
                </button>
              )}
              <button
                type='button'
                className='cms-button'
                disabled={draft.pages.length >= 30}
                onClick={() => duplicatePage()}
              >
                <Copy size={16} />
                Dupliquer en privé
              </button>
              {!inMenu ? (
                <button
                  type='button'
                  className='cms-button'
                  disabled={page.visibility === 'DRAFT'}
                  onClick={() => addToMenu()}
                >
                  <ListPlus size={16} />
                  Ajouter au menu
                </button>
              ) : (
                <span className='cms-pill'>Présente dans le menu</span>
              )}
              {page.slug !== 'home' && (
                <span className='cms-row'>
                  <button
                    type='button'
                    className='cms-icon-button'
                    aria-label='Monter la page'
                    disabled={ordered[0]?.slug === page.slug}
                    onClick={() => {
                      onChange(cmsMovePage(draft, page.slug, -1));
                      setMessage('Ordre des pages modifié, sans changer le menu.');
                    }}
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type='button'
                    className='cms-icon-button'
                    aria-label='Descendre la page'
                    disabled={ordered.at(-1)?.slug === page.slug}
                    onClick={() => {
                      onChange(cmsMovePage(draft, page.slug, 1));
                      setMessage('Ordre des pages modifié, sans changer le menu.');
                    }}
                  >
                    <ArrowDown size={16} />
                  </button>
                </span>
              )}
            </div>
            <p className='cms-hint'>
              L’ordre des pages et des cartes de découverte est indépendant du menu.{' '}
              {page.visibility === 'DRAFT'
                ? 'Cette page restera privée ; incluez-la avant de l’ajouter au menu.'
                : !inMenu
                  ? 'Cette page est incluse, mais aucun lien de menu ne la présente encore.'
                  : ''}
            </p>
            {error && (
              <div className='cms-alert' role='alert'>
                {error}
              </div>
            )}
            <details className='cms-page-settings' key={page.slug}>
              <summary>
                <Settings2 size={16} />
                Paramètres de la page
              </summary>
              <div className='cms-form-grid'>
                <label>
                  Titre de la page
                  <input
                    value={page.title}
                    maxLength={140}
                    onChange={e => updatePage({ ...page, title: e.target.value })}
                  />
                </label>
                <label>
                  Adresse de la page
                  {page.slug === 'home' ? (
                    <span className='cms-readonly'>/</span>
                  ) : (
                    <>
                      <input
                        value={addresses[page.slug] ?? page.slug}
                        maxLength={64}
                        aria-label='Adresse de la page'
                        onChange={e => setAddresses(previous => ({ ...previous, [page.slug]: e.target.value }))}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            applyAddress();
                          }
                        }}
                      />
                      {addresses[page.slug] !== undefined && addresses[page.slug].trim() !== page.slug && (
                        <span className='cms-row'>
                          <button type='button' className='cms-button' onClick={applyAddress}>
                            Appliquer l’adresse
                          </button>
                          <button
                            type='button'
                            className='cms-link-button'
                            onClick={() => setAddresses(previous => ({ ...previous, [page.slug]: page.slug }))}
                          >
                            Annuler l’adresse
                          </button>
                        </span>
                      )}
                    </>
                  )}
                  <small>
                    Les liens internes du brouillon suivent le changement. Pas de redirection des anciens liens
                    externes.
                  </small>
                </label>
                <label>
                  Diffusion
                  <select
                    aria-label='Visibilité de la page'
                    value={page.visibility ?? 'PUBLIC'}
                    disabled={page.slug === 'home'}
                    onChange={e => void visibility(e.target.value as 'PUBLIC' | 'DRAFT')}
                  >
                    <option value='PUBLIC'>Inclure à la prochaine publication</option>
                    <option value='DRAFT'>Garder cette page privée</option>
                  </select>
                  <small>La version en ligne ne change qu’après publication.</small>
                </label>
                <label className='cms-span-two'>
                  Description pour le référencement
                  <textarea
                    rows={2}
                    maxLength={320}
                    value={page.description}
                    onChange={e => updatePage({ ...page, description: e.target.value })}
                  />
                  <small>{page.description.length}/320 caractères · facultative</small>
                </label>
              </div>
              {references.length > 0 && (
                <p className='cms-hint'>
                  Références : {references.map(ref => ref.label).join(' ; ')}. Elles doivent être retirées avant une
                  suppression.
                </p>
              )}
              {page.slug !== 'home' && (
                <button type='button' className='cms-link-button cms-danger' onClick={() => void deletePage()}>
                  Supprimer cette page du brouillon
                </button>
              )}
            </details>
            {(images.isError || docs.isError || competitions.isError) && (
              <p className='cms-alert' role='alert'>
                Certaines ressources sont indisponibles. Les sélections existantes restent conservées.{' '}
                <button
                  type='button'
                  onClick={() => {
                    void images.refetch();
                    void docs.refetch();
                    void competitions.refetch();
                  }}
                >
                  Réessayer
                </button>
              </p>
            )}
            {message && (
              <p role='status' className='cms-hint'>
                {message}
              </p>
            )}
            {undo?.slug === page.slug && undo.revision === draft.revision && (
              <button
                type='button'
                className='cms-link-button'
                onClick={() => {
                  updatePage({ ...page, blocks: undoCmsStructure(page.blocks, undo.value) });
                  setUndo(null);
                  setMessage('Structure restaurée. Vos derniers textes sont conservés.');
                }}
              >
                Annuler la dernière modification de structure
              </button>
            )}
            {primaryCover && blockCard(primaryCover, coverIndex, true)}
            <div className='cms-card-heading cms-content-heading'>
              <div>
                <h3>Sections de la page</h3>
                <small>{page.blocks.length}/40 sections</small>
              </div>
              <div className='cms-row'>
                <button type='button' className='cms-link-button' onClick={() => setExpanded('__all__')}>
                  Tout déplier
                </button>
                <button type='button' className='cms-link-button' onClick={() => setExpanded('')}>
                  Tout replier
                </button>
              </div>
            </div>
            <div className='cms-block-list'>
              {page.blocks.map((block, index) =>
                index === coverIndex ? null : (
                  <div key={cmsIdentity(block)}>
                    <button
                      type='button'
                      className='cms-insert'
                      disabled={page.blocks.length >= 40}
                      aria-label={`Insérer une section avant ${index + 1}`}
                      onClick={() => setInsertion(index)}
                    >
                      <Plus size={14} />
                      Insérer ici
                    </button>
                    {blockCard(block, index)}
                  </div>
                ),
              )}
            </div>
            <button
              type='button'
              className='cms-button cms-button-soft cms-add-section'
              disabled={page.blocks.length >= 40}
              onClick={() => setInsertion(page.blocks.length)}
            >
              <Plus size={18} />
              Ajouter une section
            </button>
            {!page.blocks.length && (
              <p className='cms-empty'>Une page vide pour commencer. Ajoutez une section à partir de nos modèles.</p>
            )}
            {insertion !== null && (
              <SiteCmsDialog title='Ajouter une section' wide onClose={() => setInsertion(null)}>
                <p>La section sera insérée à l’emplacement choisi. Les exemples sont modifiables.</p>
                <div className='cms-section-choices'>
                  {sectionTypes.map(([type, title, caption]) => (
                    <button
                      key={type}
                      type='button'
                      disabled={page.slug === 'home' && type === 'cover' && Boolean(primaryCover)}
                      onClick={() => {
                        const block = newCmsSection(type);
                        structure(
                          [...page.blocks.slice(0, insertion), block, ...page.blocks.slice(insertion)],
                          'Section ajoutée. Personnalisez son contenu.',
                        );
                        setExpanded(cmsIdentity(block));
                        setInsertion(null);
                      }}
                    >
                      <Plus size={17} />
                      <span>
                        <b>{title}</b>
                        <small>{caption}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </SiteCmsDialog>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
